import * as React from 'react';
import '../assets/css/result.css';
import Banner from '../components/Banner';
import Dialog from '../components/Dialog';
import Div from '../components/Div.styled';
import DescriptionPage from '../components/HeadlineSubHeadlineText';
import SpaceLike from '../components/Space';
import {JSONMARKDOWN} from '../types/results';
import {FeatureCard} from './ResultFeatureCards';
import {CAR_MODEL} from '../constants/result';
import useMediaQuery from '../hooks/useMediaQuery';
import {ButtonCTAStyled} from '../components/CTAStyled';
import {appendQueryParams} from '../utils';
import {IFRAME_SRC} from './registerYourInterest/constants';

interface Feature {
  info?: string;
  title_line_1: string;
  title_line_2: string;
  disclaimer?: string;
}

export interface IFeatureVehicleProps {
  assetUrl?: string;
  hero_title?: string;
  hero_image_background?: string;
  hero_image_car?: string;
  hero_image_car_model?: string;
  body_title_line_1?: string;
  body_title_line_2?: string;
  body?: string;
  name?: string;
  features?: Feature[];
  carModel: string;
  handleRegisterYourInterest: () => void;
}

export default function ResultSectionOne(props: IFeatureVehicleProps) {
  const {
    assetUrl,
    name,
    hero_title,
    hero_image_background,
    hero_image_car,
    hero_image_car_model,
    body_title_line_1,
    body_title_line_2,
    body,
    features,
    carModel,
    handleRegisterYourInterest,
  } = props;
  const [openModal, setOpenModal] = React.useState(false);
  const [content, setContent] = React.useState('');
  const [title, setTitle] = React.useState('');
  const renderFeatureItem = (featureCard: Feature, i: number) => {
    return (
      <FeatureCard
        key={i}
        cardItem={featureCard}
        onClickInfoCard={() => {
          setContent(featureCard?.info || '');
          setTitle(featureCard?.title_line_1 || '');
          setOpenModal(true);
        }}
      />
    );
  };
  const {isDesktop, isTablet} = useMediaQuery();
  const isAmarok = carModel === CAR_MODEL.Amarok;
  const isTouareg = carModel === CAR_MODEL.Touareg;

  const renderCTALabel = () => {
    switch (carModel) {
      case 'id4':
      case 'id5gtx':
      case 'idBuzz5':
      case 'idBuzz7':
      case 'idBuzzCargo':
        return 'Book a test drive';
      case 'id4gtx':
        return 'Explore the ID. 4 GTX';
      case 'id5':
        return 'Explore the ID. 5 Pro';
      case 'touareg':
        return 'Request a call';
    }

    if (isAmarok) {
      return 'Book a test drive';
    }
    return 'Get in touch';
  };

  const handleCTAClick = () => {
    switch (carModel) {
      case 'id4':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.4-pro',
          }),
          '_blank',
        );
        return;
      case 'id5gtx':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.5-gtx',
          }),
          '_blank',
        );
        return;
      case 'idBuzz5':
      case 'idBuzz7':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.buzz',
          }),
          '_blank',
        );
        return;
      case 'idBuzzCargo':
        window.open(
          appendQueryParams(IFRAME_SRC, {
            SalesGroupName: 'ID.buzz-cargo',
          }),
          '_blank',
        );
        return;
      case 'id4gtx':
        window.open(
          'https://www.volkswagen.com.au/en/models/id4-gtx.html',
          '_blank',
        );
        return;
      case 'id5':
        window.open(
          'https://www.volkswagen.com.au/en/models/id5.html',
          '_blank',
        );
        return;

      default:
        break;
    }

    if (isAmarok) {
      window.open(
        'https://www.volkswagen.com.au/app/locals/test-drive?SalesGroupName=new-amarok',
        '_blank',
      );
      return;
    }

    handleRegisterYourInterest();
  };

  return (
    <Div>
      <Div
        padding={
          isAmarok ? '0 0 80px 0' : isDesktop ? '0 0 120px 0' : '0 0 86px 0'
        }
      >
        <Banner
          modelText={
            hero_image_car_model
              ? (((assetUrl as string) + hero_image_car_model) as string)
              : ''
          }
          title={
            isAmarok || isTouareg
              ? `is the better fit for you, ${name}`
              : name + ', meet the'
          }
          backgroundImage={
            ((assetUrl as string) + hero_image_background) as string
          }
          carImage={
            hero_image_car
              ? (((assetUrl as string) + hero_image_car) as string)
              : ''
          }
          titleStrong={hero_title ?? ''}
          isCarBuzz={carModel.includes(CAR_MODEL.Buzz)}
          isAmarokOrTouareg={isAmarok || isTouareg}
        />
      </Div>

      <Div
        maxWidth={'1116px'}
        margin={
          isAmarok
            ? '0 auto'
            : isDesktop
            ? '80px auto 0'
            : isTablet
            ? '67px auto 0'
            : '55px auto 0'
        }
        customStyles={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <DescriptionPage
          title={body_title_line_1 ?? ''}
          titleStrong={body_title_line_2 ?? ''}
          descriptions={body?.replace(JSONMARKDOWN.name, name ?? '')}
        />
        <Div className="section-one-scroll no-scroll">
          <SpaceLike direction="horizontal" size={16}>
            {features?.map(renderFeatureItem)}
          </SpaceLike>
        </Div>
        <Div padding="0 32px" className="flex-center">
          <ButtonCTAStyled
            tag="button"
            emphasis="primary"
            onClick={handleCTAClick}
            ariaLabel="aria label for CTA"
            size="large"
          >
            {renderCTALabel()}
          </ButtonCTAStyled>
        </Div>
      </Div>
      <Dialog
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        content={content}
        title={title}
      />
    </Div>
  );
}
